// ============================================================================
// MASTER COLOUR LIST — single source of truth for all block colour pickers
// Format: ['value', 'Label', 'lightness']
//   value     → CSS variable or hex (used as the stored value)
//   Label     → What editors see in the dropdown
//   lightness → 'light' or 'dark' (used to determine foreground/text colour)
//
// Import in block schemas and components:
//   import { MASTER_COLOR_LIST, getColorChoices, isColorDark } from '../../config/colors';
//
// To extend with block-specific colours:
//   const blockColorList = [...MASTER_COLOR_LIST, ['transparent', 'Transparent', 'light']];
//   choices: getColorChoices(blockColorList)
// ============================================================================

export const MASTER_COLOR_LIST = [
  ["#ffffff", "White", "light"],
  ["var(--deeporange)", "Deep Orange", "dark"],
  ["var(--orange)", "Orange", "dark"],
  ["var(--red)", "Red", "dark"],
  ["var(--charcoal)", "Charcoal", "dark"],
  ["var(--darkgrey)", "Dark Grey", "light"],
  ["var(--lightgrey)", "Light Grey", "light"],
];

// Strip lightness — returns [value, label] pairs for Volto select widgets
export const getColorChoices = (list = MASTER_COLOR_LIST) =>
  list.map(([value, label]) => [value, label]);

// Returns true if the colour is dark (i.e. needs light/white text over it)
export const isColorDark = (value, list = MASTER_COLOR_LIST) => {
  const match = list.find(([v]) => v === value);
  return match ? match[2] === "dark" : false;
};

// Convert a CSS variable value back to a safe key for use in class names / storage
// e.g. 'var(--primary-blue)' → 'primary-blue'
//      '#ffffff'             → 'ffffff'
export const colorValueToKey = (value) =>
  value
    .replace(/var\(--/, "")
    .replace(/\)/, "")
    .replace(/^#/, "")
    .replace(/[^a-z0-9-]+/gi, "-")
    .toLowerCase();

// Reverse: given a key, find its original CSS variable / hex value from the list
export const colorKeyToCssVar = (key, colorList = MASTER_COLOR_LIST) => {
  const match = colorList.find(([value]) => colorValueToKey(value) === key);
  return match ? match[0] : key; // fallback: return the key as-is
};

// Generate solid + outlined button choices from the master colour list.
// Format: ['solid__primary-blue', 'Primary Blue (Solid)', 'dark']
// The double-underscore separator avoids collision with CSS variable dashes.
export const getButtonChoices = (colorList = MASTER_COLOR_LIST) =>
  colorList.flatMap(([value, label, lightness]) => {
    const key = colorValueToKey(value);
    return [
      [`solid__${key}`, `${label} — Solid`, lightness],
      [`outline__${key}`, `${label} — Outlined`, lightness],
    ];
  });

// Return the most appropriate default button value for a given background colour.
// On a dark background → suggests an outlined or solid white/light colour.
// On a light background → suggests an outlined or solid dark/primary colour.
export const getDefaultButton = (
  bgColorValue,
  colorList = MASTER_COLOR_LIST,
) => {
  const bgEntry = colorList.find(([value]) => value === bgColorValue);
  const bgLightness = bgEntry?.[2] ?? "light";

  // For a dark background, find the first *light* colour (e.g. White)
  // For a light background, find the first *dark* colour (e.g. Primary)
  const targetLightness = bgLightness === "dark" ? "light" : "dark";
  const preferredColor = colorList.find(([, , l]) => l === targetLightness);
  const fallback = colorList[0];

  const key = colorValueToKey((preferredColor ?? fallback)[0]);
  return `outline__${key}`;
};

// Given a button style value (e.g. 'solid__coral') and a base class,
// returns a className string and an inline style object with --btn-color set.
//
// The className always includes:
//   - The base class (e.g. 'button', 'embla__slide-btn')
//   - 'btn-unified'              → marks it as a unified system button (target all at once)
//   - 'btn-{variant}'            → 'btn-solid' or 'btn-outline'
//   - 'btn-color-{key}'          → e.g. 'btn-color-coral', 'btn-color-ffffff'
//   - '{baseClass}--{variant}'   → e.g. 'button--solid' (for local block CSS)
//
// Usage:
//   import { getButtonClasses } from '../../../config/colors';
//   const { className, style } = getButtonClasses(item.buttonStyle, 'button');
//
// CSS targeting examples:
//   .btn-unified                          { /* all system buttons, any block */ }
//   .btn-solid                            { /* all solid buttons, any block */ }
//   .btn-outline                          { /* all outlined buttons, any block */ }
//   .btn-color-coral                      { /* coral buttons across all blocks */ }
//   .btn-solid.btn-color-ffffff           { /* solid white buttons specifically */ }
export const getButtonClasses = (value = "", baseClass = "button") => {
  if (value && value.includes("__")) {
    const [variant, colorKey] = value.split("__");
    const cssVar = colorKeyToCssVar(colorKey);
    return {
      className: [
        baseClass,
        "btn-unified",
        `btn-${variant}`,
        `btn-color-${colorKey}`,
        `${baseClass}--${variant}`,
      ].join(" "),
      style: { "--btn-color": cssVar },
    };
  }
  // Non-standard value (e.g. 'arrow') — pass through untouched
  return { className: `${baseClass} ${value}`.trim(), style: {} };
};
